import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Box, Container, useTheme } from '@mui/material'
import {
  Description,
  Person,
  Work,
  FavoriteBorder,
  School,
  Lock,
} from '@mui/icons-material'

// Import existing sections (we'll reuse them)
import BasicInformationSection from 'components/Profile/Sections/BasicInformationSection'
import ProfessionalDetailsSection from 'components/Profile/Sections/ProfessionalDetailsSection'
import BackgroundInformationSection from 'components/Profile/Sections/BackgroundInformationSection'
import PreferencesSection from 'components/Profile/Sections/PreferencesSection'
import FileSection from 'components/Profile/Sections/FileSection'
import AboutYouSection from 'components/Profile/Sections/AboutYouSection'
import PasswordSection from 'components/Profile/Sections/PasswordSection'

// Import Profile components
import {
  ProfileHeader,
  ProfileSection,
  ProfileTabs,
  ProfileSkeleton,
} from 'components/Profile'

// Redux actions
import { fetchUpdateCandidate } from 'src/redux/candidate'
import { fetchRegistrationData } from 'src/redux/supportingData'
import { NavigationBlockProvider } from 'components/Profile/Sections/useGlobalNavigationBlock'

/**
 * CandidateProfile - Modern candidate profile with enhanced UX
 * Features:
 * - Tabbed navigation
 * - Profile completion card
 * - Reuses existing section components
 * - Responsive design
 */
const CandidateProfile = () => {
  const dispatch = useDispatch()
  const theme = useTheme()

  // Redux state
  const user = useSelector((state) => state.identity)
  const candidate = useSelector((state) => state.candidate)
  const candidateTypes = useSelector((state) => state.supportingData.candidateTypes)

  // Local state
  const [formData, setFormData] = useState({})
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('file-uploads')

  // Tab definitions
  const tabs = [
    { label: 'File Uploads', icon: <Description />, id: 'file-uploads' },
    { label: 'Basic Info', icon: <Person />, id: 'basic-info' },
    { label: 'Professional', icon: <Work />, id: 'professional-details' },
    { label: 'Background', icon: <School />, id: 'background-info' },
    { label: 'Preferences', icon: <FavoriteBorder />, id: 'preferences' },
    { label: 'About You', icon: <Person />, id: 'about-you' },
    { label: 'Security', icon: <Lock />, id: 'password' },
  ]

  // Fetch supporting data on mount
  useEffect(() => {
    dispatch(fetchRegistrationData())
  }, [dispatch])

  // Initialize form data from candidate state
  useEffect(() => {
    if (!candidate.loading) {
      setFormData({
        id: user.candidateId,
        phone: candidate.phone,
        city: candidate.city,
        state: candidate.state,
        showPhone: candidate.showPhone,
        showEmail: candidate.showEmail,
        email: candidate.email,
        searchStatus: candidate.searchStatus,
        availabilityDate: candidate.availabilityDate,
        resume: candidate.resume,
        residency: candidate.residency,
        residencyEnd: candidate.residencyEnd,
        about: candidate.about,
        aboutRaw: candidate.aboutRaw,
        visaIds: candidate.visas,
        candidateLanguages: candidate.candidateLanguages || [],
        specialtyIds: candidate.specialties,
        subspecialtyIds: candidate.subspecialties,
        npiNumber: candidate.npiNumber,
        boardEligibility: candidate.boardEligibility,
        licensedStates: candidate.licensedStates,
        candidateTypeId: candidate.candidateType?.id,
        yearsExperience: candidate.yearsExperience,
        preferredStates: candidate.preferredStates || [],
        areaTypePreference: candidate.areaTypePreference || '',
        preferredPracticeTypes: candidate.preferredPracticeTypes || [],
        relocationPreference: candidate.relocationPreference,
        aboutJob: candidate.aboutJob || '',
        preferredAreaTypes: candidate.preferredAreaTypes || [],
        academicPreference: candidate.academicPreference,
        preferredGlobalRegions: candidate.preferredGlobalRegions || [],
      })
      setLoading(false)
    }
  }, [candidate, user.candidateId])

  // Save handler
  const handleSaveSection = (dataToSubmit) => {
    dispatch(fetchUpdateCandidate(dataToSubmit, 'Profile Updated!'))
  }

  if (loading) {
    return <ProfileSkeleton />
  }

  return (
    <NavigationBlockProvider>
      <Box
        sx={{
          width: '100%',
          minHeight: '100vh',
          bgcolor: theme.palette.background.default,
        }}
      >
        {/* Profile Header */}
        <ProfileHeader
          profile={{
            firstName: candidate.firstName,
            lastName: candidate.lastName,
            city: candidate.city,
            state: candidate.state,
            phone: candidate.phone,
            profileImage: candidate.profileImage,
            emailVerifiedAt: user.verifiedAt || candidate.verifiedAt,
            specialties: candidate.specialties,
            yearsExperience: candidate.yearsExperience,
            searchStatus: candidate.searchStatus,
            resume: candidate.resume,
            about: candidate.about,
            preferredStates: candidate.preferredStates,
          }}
          profileType="candidate"
        />

        {/* Tabbed Navigation */}
        <Box sx={{ position: 'sticky', top: 64, zIndex: 10, bgcolor: 'background.paper' }}>
          <Container maxWidth="lg">
            <ProfileTabs activeTab={activeTab} onChange={setActiveTab} tabs={tabs} />
          </Container>
        </Box>

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* File Uploads Section */}
          {activeTab === 'file-uploads' && (
            <ProfileSection
              id="file-uploads"
              title="File Uploads"
              icon={<Description />}
              completionStatus={
                candidate.profileImage && candidate.resume ? 'complete' : 'incomplete'
              }
            >
              <FileSection
                formData={formData}
                setFormData={setFormData}
                onSave={handleSaveSection}
                candidate={candidate}
              />
            </ProfileSection>
          )}

          {/* Basic Information Section */}
          {activeTab === 'basic-info' && (
            <ProfileSection
              id="basic-info"
              title="Basic Information"
              icon={<Person />}
              completionStatus={
                candidate.city && candidate.state && candidate.phone ? 'complete' : 'incomplete'
              }
            >
              <BasicInformationSection
                formData={formData}
                setFormData={setFormData}
                onSave={handleSaveSection}
                candidate={candidate}
              />
            </ProfileSection>
          )}

          {/* Professional Details Section */}
          {activeTab === 'professional-details' && (
            <ProfileSection
              id="professional-details"
              title="Professional Details"
              icon={<Work />}
              completionStatus={
                candidate.specialties?.length > 0 && candidate.yearsExperience
                  ? 'complete'
                  : 'incomplete'
              }
            >
              <ProfessionalDetailsSection
                formData={formData}
                setFormData={setFormData}
                onSave={handleSaveSection}
                candidateTypes={candidateTypes}
              />
            </ProfileSection>
          )}

          {/* Background Information Section */}
          {activeTab === 'background-info' && (
            <ProfileSection
              id="background-info"
              title="Background Information"
              icon={<School />}
            >
              <BackgroundInformationSection
                formData={formData}
                setFormData={setFormData}
                onSave={handleSaveSection}
              />
            </ProfileSection>
          )}

          {/* Preferences Section */}
          {activeTab === 'preferences' && (
            <ProfileSection
              id="preferences"
              title="Job Preferences"
              icon={<FavoriteBorder />}
              completionStatus={
                candidate.preferredStates?.length > 0 ? 'complete' : 'incomplete'
              }
            >
              <PreferencesSection
                formData={formData}
                setFormData={setFormData}
                onSave={handleSaveSection}
              />
            </ProfileSection>
          )}

          {/* About You Section */}
          {activeTab === 'about-you' && (
            <ProfileSection
              id="about-you"
              title="About You"
              icon={<Person />}
              completionStatus={candidate.about ? 'complete' : 'incomplete'}
            >
              <AboutYouSection
                formData={formData}
                setFormData={setFormData}
                onSave={handleSaveSection}
              />
            </ProfileSection>
          )}

          {/* Security Section */}
          {activeTab === 'password' && (
            <ProfileSection id="password" title="Security" icon={<Lock />}>
              <PasswordSection candidate={candidate} />
            </ProfileSection>
          )}
        </Container>
      </Box>
    </NavigationBlockProvider>
  )
}

export default CandidateProfile
